/**
 * API functions for operator manual check payments.
 * These endpoints are not yet in the generated SDK.
 */

import { client } from "@/hera-api/client/client.gen";

export interface ManualCheckPaymentPayment {
  id: string;
  period_start_date: string;
  period_end_date: string | null;
  fee_paid: string;
  late_fee_paid: string;
  processing_fee: string;
}

export interface ManualCheckPaymentListItem {
  manual_transaction_id: string | null;
  payment_processor_transaction_id: string | null;
  check_id: string;
  /** Physical bank check number (entered when marked received). */
  check_number: string | null;
  /** Optional note shown on the payment receipt. */
  note?: string | null;
  status: string;
  registrant_name: string | null;
  registrant_company_name: string | null;
  /** AMS company name when known for the transaction's licenses. */
  ams_company_name?: string | null;
  license_display_id: string;
  jurisdiction_name: string;
  property_address: string | null;
  license_type_name: string;
  created_at: string;
  /** Settled payment date when known (check received date or card charge date). */
  payment_date: string | null;
  total_registration_fee: string;
  total_late_fee: string;
  total_processing_fee: string;
  total_inspection_fee: string;
  license_id: string;
  payments: ManualCheckPaymentPayment[];
}

export interface ManualCheckPaymentListResponse {
  checks: ManualCheckPaymentListItem[];
}

export interface ManualCheckPaymentLicensePaymentDetail {
  id: string;
  period_start_date: string;
  period_end_date: string | null;
  fee_paid: string;
  late_fee_paid: string;
  processing_fee: string;
}

export interface ManualCheckPaymentInspectionPaymentDetail {
  id: string;
  fee_paid: string;
}

export interface ManualCheckPaymentLicenseDetail {
  license_id: string;
  license_display_id: string;
  jurisdiction_name: string;
  property_address: string | null;
  license_type_name: string;
  payments: ManualCheckPaymentLicensePaymentDetail[];
  inspection_payment: ManualCheckPaymentInspectionPaymentDetail | null;
}

export interface ManualCheckPaymentDetail {
  manual_transaction_id: string | null;
  payment_processor_transaction_id: string | null;
  check_id: string;
  /** Physical bank check number (entered when marked received). */
  check_number: string | null;
  /** Optional note shown on the payment receipt. */
  note?: string | null;
  status: string;
  registrant_name: string | null;
  registrant_company_name: string | null;
  /** AMS company name when known for the transaction's licenses. */
  ams_company_name?: string | null;
  created_at: string;
  payment_date: string | null;
  check_mailing_address: string | null;
  licenses: ManualCheckPaymentLicenseDetail[];
  total_registration_fee: string;
  total_late_fee: string;
  total_processing_fee: string;
}

export interface FinaliseCheckReceivedPaymentUpdate {
  payment_id: string;
  fee_paid: string;
  late_fee_paid: string;
  processing_fee: string;
}

export interface FinaliseCheckReceivedInspectionPaymentUpdate {
  inspection_payment_id: string;
  fee_paid: string;
}

export interface FinaliseCheckReceivedRequest {
  payment_date: string;
  payment_updates: FinaliseCheckReceivedPaymentUpdate[];
  removed_license_ids?: string[];
  inspection_payment_updates?: FinaliseCheckReceivedInspectionPaymentUpdate[];
  /** Physical check number (optional). */
  check_number?: string | null;
  /** Optional note shown on the payment receipt. */
  note?: string | null;
}

export async function getManualCheckPayments(
  jurisdictionIds?: string[],
): Promise<ManualCheckPaymentListResponse> {
  const params = new URLSearchParams();
  if (jurisdictionIds && jurisdictionIds.length > 0) {
    for (const id of jurisdictionIds) {
      params.append("jurisdiction_ids", id);
    }
  }
  const queryString = params.toString();
  const url = queryString
    ? `/operator/manual-check-payments?${queryString}`
    : "/operator/manual-check-payments";

  const result = await client.get<ManualCheckPaymentListResponse>({
    url,
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });

  if (result.error) {
    const err = result.error as { message?: string };
    throw new Error(err?.message ?? "Failed to fetch manual check payments");
  }

  return result.data as ManualCheckPaymentListResponse;
}

export async function getManualCheckPaymentDetail(
  manualTransactionId: string,
): Promise<ManualCheckPaymentDetail> {
  const result = await client.get<ManualCheckPaymentDetail>({
    url: `/operator/manual-check-payments/${manualTransactionId}`,
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });

  if (result.error) {
    const err = result.error as { message?: string };
    throw new Error(err?.message ?? "Failed to fetch check payment detail");
  }

  return result.data as ManualCheckPaymentDetail;
}

export async function cancelManualCheckPayment(
  manualTransactionId: string,
): Promise<void> {
  const result = await client.post({
    url: `/operator/manual-check-payments/${manualTransactionId}/cancel`,
    parseAs: "text",
    security: [{ scheme: "bearer", type: "http" }],
  });

  if (result.error) {
    const err = result.error as { message?: string };
    throw new Error(err?.message ?? "Failed to cancel manual check payment");
  }
}

export async function finaliseManualCheckPayment(
  manualTransactionId: string,
  body: FinaliseCheckReceivedRequest,
): Promise<void> {
  const result = await client.post({
    url: `/operator/manual-check-payments/${manualTransactionId}/finalise`,
    body,
    parseAs: "text",
    security: [{ scheme: "bearer", type: "http" }],
  });

  if (result.error) {
    const err = result.error as { message?: string };
    throw new Error(err?.message ?? "Failed to finalise check received");
  }
}

/** Registrant portal: list payments for the current registrant account. */
export async function getRegistrantPayments(): Promise<ManualCheckPaymentListResponse> {
  const result = await client.get<ManualCheckPaymentListResponse>({
    url: "/registrant/payments",
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });

  if (result.error) {
    const err = result.error as { message?: string };
    throw new Error(err?.message ?? "Failed to fetch payments");
  }

  return result.data as ManualCheckPaymentListResponse;
}

/** Registrant portal: get payment detail (view only). */
export async function getRegistrantPaymentDetail(
  manualTransactionId: string,
): Promise<ManualCheckPaymentDetail> {
  const result = await client.get<ManualCheckPaymentDetail>({
    url: `/registrant/payments/${manualTransactionId}`,
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });

  if (result.error) {
    const err = result.error as { message?: string };
    throw new Error(err?.message ?? "Failed to fetch payment detail");
  }

  return result.data as ManualCheckPaymentDetail;
}

/** Registrant portal: card/ACH payment detail (same shape as check detail). */
export async function getRegistrantPaymentProcessorDetail(
  paymentProcessorTransactionId: string,
): Promise<ManualCheckPaymentDetail> {
  const result = await client.get<ManualCheckPaymentDetail>({
    url: `/registrant/payments/payment-processor/${paymentProcessorTransactionId}`,
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });

  if (result.error) {
    const err = result.error as { message?: string };
    throw new Error(err?.message ?? "Failed to fetch payment detail");
  }

  return result.data as ManualCheckPaymentDetail;
}

/** Registrant portal: cancel an awaiting payment. */
export async function cancelRegistrantPayment(
  manualTransactionId: string,
): Promise<void> {
  const result = await client.post({
    url: `/registrant/payments/${manualTransactionId}/cancel`,
    parseAs: "text",
    security: [{ scheme: "bearer", type: "http" }],
  });

  if (result.error) {
    const err = result.error as { message?: string };
    throw new Error(err?.message ?? "Failed to cancel payment");
  }
}
